import { DynamicMediaItemType } from '@/api/types/dynamic'
import { formatToDateTime } from '@/utils/date'
import moment from 'moment'
import LGSdk from '@/utils/languageSDK/index'

export const processDynamicContent = (content: string) => {
  if (!content) return content
  // 使用正则表达式匹配 <topic> 标签
  return content.replace(/<topic>(.*?)<\/topic>/g, (match, p1) => {
    // 解析 JSON 内容
    try {
      const topicData = JSON.parse(p1)
      // 返回带有内联样式的自定义 HTML 格式
      return `<span style="color: #598CFF; margin: 0 5px;">#${topicData.topicTitle}</span>`
    } catch (error) {
      return match // 如果解析失败，则返回原始内容
    }
  })
}

interface ProcessedData {
  content: string
  audioList: DynamicMediaItemType[]
  picList: DynamicMediaItemType[]
  audioCoverList: DynamicMediaItemType[]
}

export function processDynamicData<T extends { content: string, mediaList: DynamicMediaItemType[] }>(data: T): ProcessedData {
  const dynamicData = { ...data }

  // 处理内容
  const content = dynamicData.content ? processDynamicContent(dynamicData.content) : dynamicData.content

  // 处理音频列表
  const audioList = dynamicData.mediaList.length > 0
    ? dynamicData.mediaList.filter(item => item.type === 2 && item.url)
    : []

  let picList: DynamicMediaItemType[] = []
  let audioCoverList: DynamicMediaItemType[] = []

  if (audioList.length === 0) {
    // 如果没有音频，处理图片列表
    picList = dynamicData.mediaList.length > 0
      ? dynamicData.mediaList.filter(item => item.type === 1 && item.url)
      : []
  } else {
    // 如果有音频，处理音频封面列表
    audioCoverList = dynamicData.mediaList.length > 0
      ? dynamicData.mediaList.filter(item => item.type === 1 && item.url)
      : []
  }

  // 返回处理后的数据
  return {
    content,
    picList,
    audioList,
    audioCoverList
  }
}

/**
 * 将数字格式化为简洁的字符串，适用于 TikTok 风格：千显示为K，百万显示为M。
 * 例如：1000 -> "1K"，1123 -> "1.1K"，1000000 -> "1M"，1234567 -> "1.2M"。
 * @param value - 需要格式化的数字
 * @returns 格式化后的字符串
 */
export function dynamicFormatNumber(value: number, maxDigits = 5) {
  if (!value || value === 0) {
    return 0
  }
  let formattedValue = value
  let suffix = ''

  // 转换为单位 K 或 M
  if (value >= 1000000) {
    formattedValue = Math.floor(value / 100000) / 10 // 转为 M，并向下取整保留一位小数
    suffix = 'M'
  } else if (value >= 1000) {
    formattedValue = Math.floor(value / 100) / 10 // 转为 K，并向下取整保留一位小数
    suffix = 'K'
  }

  let strValue = formattedValue.toString()
  console.log(strValue, suffix.length, strValue.length)
  // 确保总字符数不超过 maxDigits
  if (strValue.length + suffix.length > maxDigits) {
    // 去掉小数部分，仅保留整数
    strValue = Math.floor(formattedValue).toString()
  }

  return strValue + suffix
}

export const formatDynamicTime = (releaseTime: string | number): string => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone // 获取用户时区
  const releaseMoment = moment.tz(releaseTime, userTimeZone) // 将发布时间转换为用户所在时区的 moment 对象
  const now = moment.tz(userTimeZone) // 获取用户所在时区的当前时间
  const diffHours = now.diff(releaseMoment, 'hours') // 计算时间差（小时）

  if (diffHours < 24) {
    return LGSdk.t('public.timeAgo', { time: diffHours }) // 如果在24小时内，返回“X小时前”
  }

  return formatToDateTime(releaseTime) || '' // 否则使用原来的格式化方法
}
